import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import marathonPhoto from '../assets/images/marathon-photo.jpg';

const AboutPage = () => {
  const navigate = useNavigate();
  const [isBookOpen, setIsBookOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasGlidedIn, setHasGlidedIn] = useState(false);

  useEffect(() => {
    // First glide in
    const glideTimer = setTimeout(() => {
      setHasGlidedIn(true);
    }, 300);

    // Then open the book
    const openTimer = setTimeout(() => {
      setIsBookOpen(true);
    }, 1200);

    return () => {
      clearTimeout(glideTimer);
      clearTimeout(openTimer);
    };
  }, []);

  const pages = [
    {
      id: 0,
      title: "Our Story",
      content: (
        <div className="h-full flex flex-col justify-between p-4 sm:p-8">
          <div className="flex-1 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="mb-4">
                <motion.div 
                  className="inline-block text-3xl sm:text-5xl mb-3"
                  animate={{ 
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ 
                    duration: 4, 
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  📚
                </motion.div>
              </div>
              
              <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4 sm:mb-6">
                Page4Change
              </h2>
              
              <div className="space-y-3 text-gray-700 max-w-sm mx-auto">
                <p className="font-medium text-primary/80 text-sm sm:text-base">
                  Our Story
                </p>
                <p className="text-sm sm:text-base leading-relaxed">
                  As two Year 12 students from London - Michael and Francesco - we noticed the worrying trends about reading among young people. This inspired us to create Page4Change, dedicated to getting books into the hands of children from underprivileged backgrounds and helping them discover a love of reading. To raise funds for our mission, we decided to run a half marathon, pushing ourselves to make a real difference in our community.
                </p>
              </div>
              
              <motion.div
                className="mt-4 sm:mt-6 flex justify-center space-x-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                <div className="bg-primary/10 rounded-full px-3 py-1.5 text-xs sm:text-sm font-medium text-primary">
                  Year 12 Students
                </div>
                <div className="bg-secondary-green/10 rounded-full px-3 py-1.5 text-xs sm:text-sm font-medium text-secondary-green">
                  London Based
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="mt-4 sm:mt-6"
              >
                <button
                  onClick={() => setCurrentPage(1)}
                  className="bg-gradient-to-r from-primary to-purple-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105"
                >
                  See How We Fundraised →
                </button>
              </motion.div>
            </motion.div>
          </div>
          
          <div className="text-center text-xs text-gray-400 border-t pt-3">
            Page 1 of 5
          </div>
        </div>
      )
    },
    {
      id: 1,
      title: "The Half Marathon Journey",
      content: (
        <div className="h-full flex flex-col justify-between p-4 sm:p-8">
          <div className="flex-1 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="text-center mb-3 sm:mb-4">
                <h2 className="text-lg sm:text-xl font-bold text-primary mb-2 sm:mb-3">
                  The Half Marathon Journey
                </h2>
                <div className="w-10 sm:w-12 h-1 bg-gradient-to-r from-primary to-purple-500 mx-auto rounded-full"></div>
              </div>
              <div className="flex justify-center">
                <motion.div
                  className="inline-block"
                  whileHover={{ scale: 1.05 }}
                >
                  <img 
                    src={marathonPhoto} 
                    alt="Half Marathon Achievement" 
                    className="rounded-xl shadow-lg w-64 h-64 sm:w-96 sm:h-96 object-cover border-2 border-white transition-all duration-300"
                  />
                </motion.div>
              </div>
            </motion.div>
          </div>
          <div className="text-center text-xs text-gray-400 border-t pt-3">
            Page 2 of 5
          </div>
        </div>
      )
    },
    {
      id: 2,
      title: "Achievement Stats",
      content: (
        <div className="h-full flex flex-col justify-between p-4 sm:p-8">
          <div className="flex-1 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-primary mb-3 sm:mb-4">
                🏅 Achievement Stats
              </h2>
              <div className="w-12 sm:w-16 h-1 bg-gradient-to-r from-primary to-secondary-green mx-auto rounded-full mb-4"></div>
              <p className="text-gray-700 mb-4 text-xs sm:text-sm max-w-sm mx-auto leading-relaxed">
                Completing the half marathon was both challenging and unforgettable. The rain and stiff legs made every step a test of endurance.
              </p>
              <div className="flex justify-center">
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-3.5 sm:p-4.5 shadow-lg border border-gray-100 max-w-[440px] w-full">
                  <div className="grid grid-cols-2 gap-2.5 sm:gap-3.5 text-center mb-2.5 sm:mb-3.5">
                    <div className="bg-primary/5 rounded-xl p-2.5">
                      <div className="text-lg sm:text-xl font-bold text-primary">21.8</div>
                      <div className="text-xs text-gray-500">km</div>
                    </div>
                    <div className="bg-secondary-green/5 rounded-xl p-2.5">
                      <div className="text-lg sm:text-xl font-bold text-secondary-green">6:07</div>
                      <div className="text-xs text-gray-500">min/km</div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-2.5 sm:p-3.5 text-center">
                    <div className="text-lg sm:text-xl font-bold text-purple-600">£10,000</div>
                    <div className="text-xs text-gray-500">Raised for AllChild</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
          <div className="text-center text-xs text-gray-400 border-t pt-3">
            Page 3 of 5
          </div>
        </div>
      )
    },
    {
      id: 3,
      title: "Student Testimonial",
      content: (
        <div className="h-full flex flex-col justify-between p-4 sm:p-8">
          <div className="flex-1 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="mb-4">
                <div className="text-3xl sm:text-4xl mb-3">💭</div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-2">
                  What Students Say
                </h2>
                <div className="w-12 sm:w-16 h-1 bg-gradient-to-r from-primary to-secondary-green mx-auto rounded-full"></div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-3.5 sm:p-5.5 shadow-lg border border-blue-100 max-w-[440px] mx-auto">
                <div className="text-2xl sm:text-3xl text-primary mb-2.5 sm:mb-3.5">"</div>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3.5 sm:mb-4.5">
                  "I used to think reading was boring, but Page4Change made it fun! The books they have are actually interesting, and I love being able to chat about them with my friends. My favorite part is when we get to discuss the stories in class."
                </p>
                <div className="border-t border-blue-200 pt-2.5 sm:pt-3.5">
                  <p className="font-semibold text-primary text-sm sm:text-base">Sarah Johnson</p>
                  <p className="text-xs text-gray-500">Year 8 Student, Preston Manor</p>
                </div>
              </div>
              
              <div className="mt-4 flex justify-center space-x-1">
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    className="text-yellow-400 text-lg"
                  >
                    ⭐
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
          <div className="text-center text-xs text-gray-400 border-t pt-3">
            Page 4 of 5
          </div>
        </div>
      )
    },
    {
      id: 4,
      title: "Our Partners",
      content: (
        <div className="h-full flex flex-col justify-between p-4 sm:p-8">
          <div className="flex-1 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="mb-4">
                <div className="text-3xl sm:text-4xl mb-3">🤝</div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-2">
                  Our Partners
                </h2>
                <div className="w-12 sm:w-16 h-1 bg-gradient-to-r from-primary to-secondary-green mx-auto rounded-full"></div>
              </div>
              
              <div className="space-y-6 max-w-sm mx-auto">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 sm:p-5 shadow-lg border border-blue-100">
                  <h3 className="font-semibold text-primary mb-2">Literacy Pirates</h3>
                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">
                    We're proud to partner with Literacy Pirates, an organisation dedicated to building literacy and confidence in young readers.
                  </p>
                  <a 
                    href="https://literacypirates.org" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 text-sm font-medium inline-flex items-center"
                  >
                    Visit Literacy Pirates <span className="ml-1">→</span>
                  </a>
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-3 sm:p-4 shadow-lg border border-purple-100">
                  <h3 className="font-semibold text-primary mb-1">AllChild</h3>
                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-2">
                    Our partnership with AllChild helps us reach more children and young people in underserved communities. Their Link Workers provide crucial support to ensure every child has the opportunity to flourish.
                  </p>
                  <a 
                    href="https://www.allchild.org" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 text-sm font-medium inline-flex items-center"
                  >
                    Visit AllChild <span className="ml-1">→</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
          <div className="text-center text-xs text-gray-400 border-t pt-3">
            Page 5 of 5
          </div>
        </div>
      )
    }
  ];

  const nextPage = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-2 sm:p-4">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ 
          scale: hasGlidedIn ? 1 : 0.8,
          opacity: hasGlidedIn ? 1 : 0
        }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative w-full max-w-sm sm:max-w-md lg:max-w-xl bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden"
        style={{ 
          height: 'calc(100vh - 2rem)',
          maxHeight: '700px',
          minHeight: '550px'
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="h-full flex flex-col"
            style={{ paddingBottom: '60px' }}
          >
            {pages[currentPage].content}
          </motion.div>
        </AnimatePresence>

        <div className="absolute bottom-0 left-0 right-0 flex justify-center space-x-3 bg-gradient-to-t from-white via-white/95 to-transparent py-3 px-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={prevPage}
            disabled={currentPage === 0}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              currentPage === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary/90 shadow-md'
            }`}
          >
            Previous
          </motion.button>
          
          <div className="flex space-x-1 items-center">
            {pages.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentPage 
                    ? 'bg-primary w-6' 
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={nextPage}
            disabled={currentPage === pages.length - 1}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              currentPage === pages.length - 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary/90 shadow-md'
            }`}
          >
            Next
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default AboutPage; 